import React, { useCallback, useEffect, useState } from "react";

import {
  FaBars,
  FaClipboardCheck,
  FaCrown,
  FaFileInvoiceDollar,
  FaTasks,
  FaUserPlus,
  FaUsers,
  FaRegClipboard,
} from "react-icons/fa";

import { useDispatch, useSelector } from "react-redux";

import { useHistory } from "react-router-dom";

import ChatBtn from "../common/ChatBtn";

import AdminSidebar from "./components/Navbar/AdminSidebar";

import SubscriptionPopup from "../../components/dashboard/components/AdminComponent/subscription/SubscriptionPopup";

import API from "../../utils/apiCalling";
import { config } from "../../utils/apiUrl";
import useSwal from "../../hooks/useSwal";
import useAuth from "../../hooks/useAuth";

import AddEditTask from "../../components/dashboard/components/AdminComponent/TaskManagement/AddEditTask";
import AddCustomerDialog from "../../components/dashboard/components/AdminComponent/CustomerService/RequirementManagement/AddCustomerDialog";
import AddEditRequirement from "../../components/dashboard/components/AdminComponent/CustomerService/RequirementManagement/AddEditRequirement";

import { getCurrentSubscription } from "../../store/tenantSubscription/subscriptionSlice";

import "./DashboardLayout.css";
import "./DashboardHeaderActions.css";

import "../../components/dashboard/components/AdminComponent/TaskManagement/TaskManagement.css";
import "../../components/dashboard/components/AdminComponent/CustomerService/RequirementManagement/RequirementManagement.css";

const DashboardLayout = ({
  children,

  showSidebar = true,

  showHeader = true,

  showChat = true,

  defaultSidebarOpen = false,

  className = "",
}) => {
  const dispatch = useDispatch();

  const history = useHistory();

  const [api] = useState(() => new API());

  const swal = useSwal();

  const { permissionCodes = [], isOwner, user } = useAuth();
  const normalizedPermissionCodes = new Set(
    (Array.isArray(permissionCodes) ? permissionCodes : [])
      .map((code) =>
        String(code || "")
          .trim()
          .toLowerCase(),
      )
      .filter(Boolean),
  );

  /*
  |--------------------------------------------------------------------------
  | HEADER ACTION STATE
  |--------------------------------------------------------------------------
  */

  const [taskFormOpen, setTaskFormOpen] = useState(false);

  const [customerFormOpen, setCustomerFormOpen] = useState(false);

  const [requirementFormOpen, setRequirementFormOpen] = useState(false);

  const [attendanceAction, setAttendanceAction] = useState({
    loading: true,

    available: true,

    state: "NOT_CHECKED_IN",

    checkIn: null,

    checkOut: null,

    employee: null,

    message: "",
  });

  const [attendanceElapsedSeconds, setAttendanceElapsedSeconds] = useState(0);

  /*
  |--------------------------------------------------------------------------
  | SUBSCRIPTION REDUX
  |--------------------------------------------------------------------------
  */

  const { currentSubscription, initialized } = useSelector(
    (state) => state?.tenantSubscription || {},
  );

  /*
  |--------------------------------------------------------------------------
  | DESKTOP SIDEBAR
  |--------------------------------------------------------------------------
  */

  const [sidebarPinnedOpen, setSidebarPinnedOpen] =
    useState(defaultSidebarOpen);

  /*
  |--------------------------------------------------------------------------
  | MOBILE SIDEBAR
  |--------------------------------------------------------------------------
  */

  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);

  /*
  |--------------------------------------------------------------------------
  | SUBSCRIPTION POPUP DISMISSED
  |--------------------------------------------------------------------------
  |
  | IMPORTANT:
  |
  | We intentionally DO NOT save this in localStorage/sessionStorage.
  |
  | Result:
  |
  | Close popup
  | → stays closed during current page lifecycle
  |
  | Browser refresh
  | → state resets
  | → subscription checked again
  | → popup opens again when subscription is missing
  |
  |--------------------------------------------------------------------------
  */

  const [subscriptionPopupDismissed, setSubscriptionPopupDismissed] =
    useState(false);

  /*
  |--------------------------------------------------------------------------
  | MANUAL SUBSCRIPTION POPUP
  |--------------------------------------------------------------------------
  |
  | Used when user clicks Subscribe button.
  |
  |--------------------------------------------------------------------------
  */

  const [manualSubscriptionOpen, setManualSubscriptionOpen] = useState(false);

  /*
  |--------------------------------------------------------------------------
  | LOAD CURRENT SUBSCRIPTION
  |--------------------------------------------------------------------------
  |
  | Only DashboardLayout performs the initial subscription check.
  |
  | IMPORTANT:
  |
  | TenantSubscriptionPage in embedded mode should NOT perform another
  | getCurrentSubscription() on mount.
  |
  |--------------------------------------------------------------------------
  */

  useEffect(() => {
    if (!initialized && !currentSubscription?.loading) {
      dispatch(getCurrentSubscription());
    }
  }, [dispatch, initialized, currentSubscription?.loading]);

  /*
  |--------------------------------------------------------------------------
  | CURRENT SUBSCRIPTION
  |--------------------------------------------------------------------------
  */

  const subscription = currentSubscription?.data || null;

  /*
  |--------------------------------------------------------------------------
  | SUBSCRIPTION STATUS
  |--------------------------------------------------------------------------
  */

  const subscriptionStatus = String(subscription?.status || "")
    .trim()
    .toUpperCase();

  /*
  |--------------------------------------------------------------------------
  | VALID SUBSCRIPTION
  |--------------------------------------------------------------------------
  |
  | Tenant is considered subscribed when:
  |
  | TRIAL
  | ACTIVE
  |
  | PENDING / CANCELLED / EXPIRED etc. are NOT treated as active.
  |
  |--------------------------------------------------------------------------
  */

  const hasValidSubscription =
    Boolean(subscription) && ["TRIAL", "ACTIVE"].includes(subscriptionStatus);

  /*
  |--------------------------------------------------------------------------
  | HEADER QUICK ACTION ACCESS
  |--------------------------------------------------------------------------
  |
  | Match backend behavior:
  | active subscription module + effective user permission.
  |
  | OWNER bypasses user permission, but not subscription module availability.
  |
  |--------------------------------------------------------------------------
  */

  const subscriptionModuleCodes = new Set(
    (Array.isArray(subscription?.modules) ? subscription.modules : [])
      .filter((module) => module?.enabled !== false)
      .map((module) =>
        String(module?.code || "")
          .trim()
          .toUpperCase(),
      )
      .filter(Boolean),
  );

  const hasHeaderActionAccess = (moduleCode, permissionCode) => {
    if (!hasValidSubscription) {
      return false;
    }

    const normalizedModule = String(moduleCode || "")
      .trim()
      .toUpperCase();

    const normalizedPermission = String(permissionCode || "")
      .trim()
      .toLowerCase();

    if (!subscriptionModuleCodes.has(normalizedModule)) {
      return false;
    }

    if (isOwner) {
      return true;
    }

    return normalizedPermissionCodes.has(normalizedPermission);
  };

  const canCreateSale = hasHeaderActionAccess("BILLING", "sale_invoice.create");

  const canCreateCustomer = hasHeaderActionAccess(
    "CUSTOMER",
    "customer.create",
  );

  const canCreateRequirement = hasHeaderActionAccess(
    "CUSTOMER",
    "requirement.create",
  );

  const canCreateTask = hasHeaderActionAccess("TASK", "task.create");

  const canUseSelfAttendance = hasHeaderActionAccess(
    "EMPLOYEE",
    "employee.attendance.create",
  );

  /*
  |--------------------------------------------------------------------------
  | AUTOMATIC POPUP
  |--------------------------------------------------------------------------
  |
  | IMPORTANT FIX:
  |
  | DO NOT use:
  |
  | !currentSubscription?.loading
  |
  | here.
  |
  | A later subscription refresh can temporarily set loading=true.
  | If popup visibility depends on loading, React unmounts the popup and
  | mounts it again after loading finishes. That causes blinking.
  |
  |--------------------------------------------------------------------------
  */

  const shouldAutoOpenSubscription =
    initialized && !hasValidSubscription && !subscriptionPopupDismissed;

  /*
  |--------------------------------------------------------------------------
  | FINAL POPUP VISIBILITY
  |--------------------------------------------------------------------------
  */

  const showSubscriptionPopup =
    manualSubscriptionOpen || shouldAutoOpenSubscription;

  /*
  |--------------------------------------------------------------------------
  | OPEN SUBSCRIPTION POPUP
  |--------------------------------------------------------------------------
  */

  const handleOpenSubscriptionPopup = () => {
    setSubscriptionPopupDismissed(false);

    setManualSubscriptionOpen(true);
  };

  /*
  |--------------------------------------------------------------------------
  | CLOSE SUBSCRIPTION POPUP
  |--------------------------------------------------------------------------
  */

  const handleCloseSubscriptionPopup = () => {
    setManualSubscriptionOpen(false);

    setSubscriptionPopupDismissed(true);
  };

  /*
  |--------------------------------------------------------------------------
  | SUBSCRIPTION SUCCESS
  |--------------------------------------------------------------------------
  |
  | Called by SubscriptionPopup/TenantSubscriptionPage after:
  |
  | - successful free trial
  | - successful Razorpay verification
  | - already-paid subscription resolution
  |
  |--------------------------------------------------------------------------
  */

  const handleSubscriptionSuccess = async () => {
    try {
      /*
        |--------------------------------------------------------------------------
        | REFRESH CURRENT SUBSCRIPTION
        |--------------------------------------------------------------------------
        */

      await dispatch(getCurrentSubscription());
    } finally {
      /*
        |--------------------------------------------------------------------------
        | CLOSE POPUP
        |--------------------------------------------------------------------------
        */

      setManualSubscriptionOpen(false);

      setSubscriptionPopupDismissed(true);
    }
  };

  /*
  |--------------------------------------------------------------------------
  | LAYOUT CLASS
  |--------------------------------------------------------------------------
  */

  const layoutClassName = [
    "dashboard-layout",

    showSidebar
      ? "dashboard-layout--with-sidebar"
      : "dashboard-layout--without-sidebar",

    showHeader
      ? "dashboard-layout--with-header"
      : "dashboard-layout--without-header",

    showSidebar && sidebarPinnedOpen
      ? "dashboard-layout--sidebar-pinned"
      : "dashboard-layout--sidebar-collapsed",

    className,
  ]
    .filter(Boolean)
    .join(" ");

  /*
  |--------------------------------------------------------------------------
  | SIDEBAR MENU BUTTON
  |--------------------------------------------------------------------------
  */

  const handleMenuButtonClick = () => {
    /*
      |--------------------------------------------------------------------------
      | DESKTOP
      |--------------------------------------------------------------------------
      */

    if (window.innerWidth >= 992) {
      setSidebarPinnedOpen((previous) => !previous);

      return;
    }

    /*
      |--------------------------------------------------------------------------
      | MOBILE
      |--------------------------------------------------------------------------
      */

    setMobileSidebarOpen(true);
  };

  /*
  |--------------------------------------------------------------------------
  | ATTENDANCE HELPERS
  |--------------------------------------------------------------------------
  */

  const applyAttendanceResponse = useCallback((response) => {
    const data = response?.data || {};

    setAttendanceAction({
      loading: false,

      available: data?.available !== false,

      state: data?.state || "NOT_CHECKED_IN",

      checkIn: data?.checkIn || data?.attendance?.checkIn || null,

      checkOut: data?.checkOut || data?.attendance?.checkOut || null,

      employee: data?.employee || null,

      message: "",
    });
  }, []);

  const loadMyAttendance = useCallback(async () => {
    try {
      setAttendanceAction((previous) => ({
        ...previous,

        loading: true,
      }));

      const response = await api.get(config.getMyAttendanceStatus);

      if (response?.status !== "success") {
        throw new Error(response?.message || "Unable to load attendance.");
      }

      applyAttendanceResponse(response);
    } catch (requestError) {
      const message =
        requestError?.response?.data?.message ||
        requestError?.message ||
        "Attendance is unavailable.";

      setAttendanceAction({
        loading: false,

        available: false,

        state: "NOT_CHECKED_IN",

        checkIn: null,

        checkOut: null,

        employee: null,

        message,
      });
    }
  }, [api, applyAttendanceResponse]);

  useEffect(() => {
    if (!canUseSelfAttendance) {
      setAttendanceAction((previous) => ({
        ...previous,

        loading: false,

        available: false,
      }));

      return;
    }

    loadMyAttendance();
  }, [canUseSelfAttendance, loadMyAttendance]);

  /*
  |--------------------------------------------------------------------------
  | LIVE CHECK-IN TIMER
  |--------------------------------------------------------------------------
  |
  | Timer is calculated from the server checkIn timestamp.
  | Refreshing the browser therefore does not restart the timer.
  |
  |--------------------------------------------------------------------------
  */

  useEffect(() => {
    if (attendanceAction.state !== "CHECKED_IN" || !attendanceAction.checkIn) {
      setAttendanceElapsedSeconds(0);

      return undefined;
    }

    const checkInTime = new Date(attendanceAction.checkIn).getTime();

    if (Number.isNaN(checkInTime)) {
      setAttendanceElapsedSeconds(0);

      return undefined;
    }

    const updateTimer = () => {
      setAttendanceElapsedSeconds(
        Math.max(
          0,

          Math.floor((Date.now() - checkInTime) / 1000),
        ),
      );
    };

    updateTimer();

    const timer = window.setInterval(updateTimer, 1000);

    return () => {
      window.clearInterval(timer);
    };
  }, [attendanceAction.state, attendanceAction.checkIn]);

  const formatAttendanceDuration = (seconds) => {
    const safeSeconds = Math.max(Number(seconds) || 0, 0);

    const hours = Math.floor(safeSeconds / 3600);

    const minutes = Math.floor((safeSeconds % 3600) / 60);

    const remainingSeconds = safeSeconds % 60;

    return [hours, minutes, remainingSeconds]
      .map((item) => String(item).padStart(2, "0"))
      .join(":");
  };

  const handleAttendanceAction = async () => {
    if (
      attendanceAction.loading ||
      !attendanceAction.available ||
      attendanceAction.state === "CHECKED_OUT"
    ) {
      return;
    }

    const isCheckingOut = attendanceAction.state === "CHECKED_IN";

    try {
      setAttendanceAction((previous) => ({
        ...previous,

        loading: true,
      }));

      const response = isCheckingOut
        ? await api.post(config.checkOutMyAttendance, {})
        : await api.post(config.checkInMyAttendance, {});

      if (response?.status !== "success") {
        throw new Error(
          response?.message ||
            (isCheckingOut ? "Unable to check out." : "Unable to check in."),
        );
      }

      applyAttendanceResponse(response);

      await swal.success({
        icon: "success",

        title: isCheckingOut ? "Checked Out" : "Checked In",

        text:
          response?.message ||
          (isCheckingOut
            ? "Attendance check-out completed."
            : "Attendance check-in completed."),

        timer: 1200,

        showConfirmButton: false,
      });
    } catch (requestError) {
      const message =
        requestError?.response?.data?.message ||
        requestError?.message ||
        "Unable to update attendance.";

      setAttendanceAction((previous) => ({
        ...previous,

        loading: false,
      }));

      swal.error({
        icon: "error",

        title: isCheckingOut ? "Check-Out Failed" : "Check-In Failed",

        text: message,
      });
    }
  };

  /*
  |--------------------------------------------------------------------------
  | HEADER QUICK ACTIONS
  |--------------------------------------------------------------------------
  */

  const headerQuickActions = [
    {
      key: "sale",

      label: "Sale",

      icon: FaFileInvoiceDollar,

      variant: "sale",

      allowed: canCreateSale,

      action: () => history.push("/billing/sale-invoices/create"),
    },
    {
      key: "customer",

      label: "Customer",

      icon: FaUserPlus,

      variant: "customer",

      allowed: canCreateCustomer,

      action: () => setCustomerFormOpen(true),
    },
    {
      key: "requirement",

      label: "Requirement",

      icon: FaRegClipboard,

      variant: "requirement",

      allowed: canCreateRequirement,

      action: () => setRequirementFormOpen(true),
    },
    {
      key: "task",

      label: "Task",

      icon: FaTasks,

      variant: "task",

      allowed: canCreateTask,

      action: () => setTaskFormOpen(true),
    },
  ];

  const attendanceButtonLabel = attendanceAction.loading
    ? "Attendance"
    : attendanceAction.state === "CHECKED_IN"
      ? `Check-Out • ${formatAttendanceDuration(attendanceElapsedSeconds)}`
      : attendanceAction.state === "CHECKED_OUT"
        ? "Completed"
        : "Check-In";

  const attendanceButtonTitle =
    attendanceAction.message ||
    (attendanceAction.state === "CHECKED_OUT"
      ? "Today's attendance is completed."
      : attendanceAction.employee?.name
        ? `Attendance • ${attendanceAction.employee.name}`
        : "Attendance");

  /*
  |--------------------------------------------------------------------------
  | RENDER
  |--------------------------------------------------------------------------
  */

  return (
    <div className={layoutClassName}>
      {/* ================================================================
          SIDEBAR
      ================================================================ */}

      {showSidebar && (
        <AdminSidebar
          pinnedOpen={sidebarPinnedOpen}
          onPinnedOpenChange={setSidebarPinnedOpen}
          mobileOpen={mobileSidebarOpen}
          onMobileOpenChange={setMobileSidebarOpen}
        />
      )}

      {/* ================================================================
          TOPBAR
      ================================================================ */}

      {showHeader && (
        <header className="dashboard-topbar">
          {/* ------------------------------------------------------------
                LEFT
            ------------------------------------------------------------ */}

          <div className="dashboard-topbar__left">
            {showSidebar && (
              <button
                type="button"
                className="dashboard-topbar__menu-btn"
                onClick={handleMenuButtonClick}
                aria-label="Toggle navigation"
              >
                <FaBars />
              </button>
            )}

            <div className="dashboard-topbar__page-info">
              <span className="dashboard-topbar__eyebrow">{user?.tenant?.code || "COMKWIK"}</span>

              <strong>Admin Console</strong>
            </div>
          </div>

          {/* ------------------------------------------------------------
                RIGHT
            ------------------------------------------------------------ */}

          <div className="dashboard-topbar__right">
            {/* ========================================================
                  SUBSCRIBE BUTTON
              ========================================================
              |
              | IMPORTANT:
              |
              | Once subscription state has initialized and tenant does
              | not have TRIAL/ACTIVE subscription, show this button.
              |
              | Do NOT depend on currentSubscription.loading.
              |
              ======================================================== */}

            {initialized && !hasValidSubscription ? (
              <button
                type="button"
                className="dashboard-topbar__subscribe-btn"
                onClick={handleOpenSubscriptionPopup}
              >
                <FaCrown />

                <span>Subscribe</span>
              </button>
            ) : null}

            {/* ========================================================
                  QUICK ACTION BUTTONS
              ======================================================== */}

            <div className="dashboard-topbar__quick-actions">
              {headerQuickActions
                .filter((action) => action.allowed)
                .map((action) => {
                  const ActionIcon = action.icon;

                  return (
                    <button
                      key={action.key}
                      type="button"
                      className={`dashboard-topbar__quick-action-btn dashboard-topbar__quick-action-btn--${action.variant}`}
                      onClick={action.action}
                    >
                      <ActionIcon />

                      <span>{action.label}</span>
                    </button>
                  );
                })}
            </div>

            {/* ========================================================
                  ATTENDANCE MINI PANEL
              ======================================================== */}

            {canUseSelfAttendance ? (
              <button
                type="button"
                className={[
                  "dashboard-topbar__attendance-panel",

                  attendanceAction.state === "CHECKED_IN"
                    ? "is-checked-in"
                    : "",

                  attendanceAction.state === "CHECKED_OUT"
                    ? "is-completed"
                    : "",
                ]
                  .filter(Boolean)
                  .join(" ")}
                onClick={handleAttendanceAction}
                disabled={
                  attendanceAction.loading ||
                  !attendanceAction.available ||
                  attendanceAction.state === "CHECKED_OUT"
                }
                title={attendanceButtonTitle}
              >
                <span className="dashboard-topbar__attendance-icon">
                  <FaClipboardCheck />
                </span>

                <span className="dashboard-topbar__attendance-copy">
                  <small>
                    {attendanceAction.state === "CHECKED_IN"
                      ? "Working now"
                      : attendanceAction.state === "CHECKED_OUT"
                        ? "Attendance"
                        : "Attendance"}
                  </small>

                  <strong>
                    {attendanceAction.loading
                      ? "Loading..."
                      : attendanceAction.state === "CHECKED_IN"
                        ? "Check-Out"
                        : attendanceAction.state === "CHECKED_OUT"
                          ? "Completed"
                          : "Check-In"}
                  </strong>
                </span>

                {attendanceAction.state === "CHECKED_IN" ? (
                  <span className="dashboard-topbar__attendance-timer">
                    {formatAttendanceDuration(attendanceElapsedSeconds)}
                  </span>
                ) : null}

                <span className="dashboard-topbar__attendance-status-dot" />
              </button>
            ) : null}
          </div>
        </header>
      )}

      {/* ================================================================
          MAIN CONTENT
      ================================================================ */}

      <main className="dashboard-main">
        <div className="dashboard-main__inner">{children}</div>
      </main>

      {/* ================================================================
          CHAT
      ================================================================ */}

      {/* {showChat && <ChatBtn />} */}

      {/* ================================================================
          SUBSCRIPTION POPUP
      ================================================================
      |
      | IMPORTANT:
      |
      | DashboardLayout only renders SubscriptionPopup.
      |
      | It does NOT render TenantSubscriptionPage directly.
      |
      ================================================================ */}

      {showSubscriptionPopup && (
        <SubscriptionPopup
          onClose={handleCloseSubscriptionPopup}
          onSubscriptionSuccess={handleSubscriptionSuccess}
        />
      )}

      {/* ================================================================
          HEADER ACTION MODALS
      ================================================================ */}

      <AddEditTask
        open={taskFormOpen}
        task={null}
        sourceContext={null}
        onClose={() => setTaskFormOpen(false)}
        onSuccess={() => setTaskFormOpen(false)}
      />

      <AddCustomerDialog
        open={customerFormOpen}
        initialValues={{
          customerName: "",

          mobile: "",
        }}
        onClose={() => setCustomerFormOpen(false)}
        onSuccess={() => setCustomerFormOpen(false)}
      />

      <AddEditRequirement
        open={requirementFormOpen}
        requirement={null}
        onClose={() => setRequirementFormOpen(false)}
        onSuccess={() => setRequirementFormOpen(false)}
      />
    </div>
  );
};

export default DashboardLayout;
